import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { IoIosEye } from "react-icons/io";
import { IoIosEyeOff } from "react-icons/io";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { loginSchema } from "@/utils/schemaHelper";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { useNavigate } from "react-router-dom";
import { loginUser } from "@/modules/authenticationService";

import { ToastSuccessMessages } from "@/enums/errorMessages";
import { ROUTES } from "@/enums/routes";
import { TiTick } from "react-icons/ti";

import { useDispatch, useSelector } from "react-redux";
import { login } from "@/store/redux/authSlice";

import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import { ErrorHelper } from "@/utils/errorHelper";

import { fetchAndSetUserData } from "@/utils/authHelper";

import { getLocalStorageItem } from "@/utils/localStorageHelper";
import { localStorageKey } from "@/enums/localStorageKey";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";

const LoginComponent = () => {
  const dispatch = useDispatch();

  const loading = useSelector((state: RootState) => state.app.loading);

  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    if (loading) return;

    dispatch(setLoading(true));

    try {
      const response = await loginUser(values);

      dispatch(
        login({
          //@ts-expect-error:skip next line
          accessToken: response.tokens.access,
          //@ts-expect-error:skip next line
          refreshToken: response.tokens.refresh,
        }),
      );

      await fetchAndSetUserData(response.access, dispatch, navigate);
      toastMessage("success", ToastSuccessMessages.LOGIN_SUCCESS);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };
  useEffect(() => {
    if (getLocalStorageItem(localStorageKey.ACCESS_TOKEN) != null) {
      navigate(ROUTES.DASHBOARD);
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center w-screen bg-gray-100 px-4 ">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-center text-2xl font-semibold mb-2 text-gray-800">
          Login
        </h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Username</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        className={`${
                          field.value && !form.formState.errors.username
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.username && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.password
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.password && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                    <div
                      className="absolute right-7 top-2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <p
              onClick={handleForgotPassword}
              className="cursor-pointer underline"
            >
              Forgot Password
            </p>

            <div className="flex justify-center ">
              <ReusableButton
                label="Log In"
                className="w-full"
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
export default LoginComponent;
